import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { successSchema } from '../utils';
import { ZPrizeCore, ZPrizeSelect, ZProductSelect } from './models';

const c = initContract();

const ZWinningCriterion = z.object({
  tier: z.string(),
  matchesRequired: z.record(z.string(), z.string()),
  isBoostRequired: z.boolean(),
});
const ZGetWinningCriteriaOfPrizeResponseV2 = z.record(
  z.string(), // drawTypeCode
  z.record(
    z.string(), // drawScheduleCode
    ZWinningCriterion,
  ),
);
export type TWinningCriterion = z.infer<typeof ZWinningCriterion>;
export type TGetWinningCriteriaOfPrizeResponseV2 = z.infer<
  typeof ZGetWinningCriteriaOfPrizeResponseV2
>;

const ZGetAllAvailablePrizesResponse = ZPrizeCore.extend({
  categories: z.string().array(),
  mainCategories: z.string().array(),
  tierCode: z.string(),
  tierValue: z.number().nullable(),
  drawTypePlans: z.string().array(),
}).array();
export type TGetAllAvailablePrizesResponse = z.infer<
  typeof ZGetAllAvailablePrizesResponse
>;

const ZGroupedGetAllAvailablePrizesResponse = z.record(
  z.string(),
  ZGetAllAvailablePrizesResponse,
);
export type TGroupedGetAllAvailablePrizesResponse = z.infer<
  typeof ZGroupedGetAllAvailablePrizesResponse
>;

const ZGetAvailablePrizeTiersResponse = z.string().array();
export type TGetAvailablePrizeTiersResponse = z.infer<
  typeof ZGetAvailablePrizeTiersResponse
>;

const ZGetCategoriesResponse = z.record(z.string(), z.string().array());
export type TGetCategoriesResponse = z.infer<typeof ZGetCategoriesResponse>;

const ZGetPrizeBySlugResponse = ZPrizeSelect.pick({
  id: true,
  title: true,
  subheading: true,
  type: true,
  slug: true,
  imageLocations: true,
  overviewBody: true,
  whatsIncludedHeading: true,
  whatsIncludedBody: true,
  seoMetaTitle: true,
  seoMetaDescription: true,
  seoKeywords: true,
  seoImageAltText: true,
  isFromPlayersSuggestion: true,
  subscriberDiscountContentCode: true,
}).extend({
  visible: z.boolean(),
  products: z.array(
    ZProductSelect.pick({
      id: true,
      name: true,
      description: true,
      imageLocation: true,
    }),
  ),
  categories: z.string().array(),
});
const ZGetPrizeBySlugResponseV2 = ZGetPrizeBySlugResponse.extend({
  winningCriteria: ZGetWinningCriteriaOfPrizeResponseV2,
});
export type TGetPrizeBySlugResponse = z.infer<typeof ZGetPrizeBySlugResponse>;
export type TGetPrizeBySlugResponseV2 = z.infer<
  typeof ZGetPrizeBySlugResponseV2
>;

const ZSimilarPrize = ZPrizeSelect.pick({
  id: true,
  title: true,
  subheading: true,
  slug: true,
}).extend({
  heroImageLocation: z.string(),
});
const ZGetSimilarPrizesResponse = ZSimilarPrize.array();
export type TGetSimilarPrizesResponse = z.infer<
  typeof ZGetSimilarPrizesResponse
>;

export const publicPrizeContract = c.router(
  {
    getAllAvailablePrizesV2: {
      method: 'GET',
      path: '/get-all-available-prizes-v2',
      query: z.object({
        drawTypeCode: z.string(),
        drawScheduleCode: z.string(),
      }),
      responses: {
        200: ZGetAllAvailablePrizesResponse,
      },
      summary:
        'Get all available prizes for prizes page, includes prize tiers and relevant draw type plans',
    },

    getGroupedPrizes: {
      method: 'GET',
      path: '/get-grouped-available-prizes',
      query: z.object({
        drawTypeCode: z.string(),
        drawScheduleCode: z.string(),
        groupBy: z.union([z.literal('mainCategory'), z.literal('tierCode')]),
        count: z.coerce.number(),
      }),
      responses: {
        200: ZGroupedGetAllAvailablePrizesResponse,
      },
      summary:
        'Get all available prizes for prizes page, includes prize tiers and relevant draw type plans',
    },

    getAvailablePrizeTiersV2: {
      method: 'GET',
      path: '/get-available-prize-tiers-v2',
      query: z.object({
        drawTypeCode: z.string(),
        drawScheduleCode: z.string(),
      }),
      responses: {
        200: ZGetAvailablePrizeTiersResponse,
        404: z.object({
          message: z.string(),
        }),
      },
      summary: 'Get all available prize tiers for a draw schedule',
    },

    getCategories: {
      method: 'GET',
      path: '/get-categories',
      responses: {
        200: ZGetCategoriesResponse,
        404: z.object({
          message: z.string(),
        }),
      },
      summary:
        'Get categories for prizes page, includes only active categories and those with more than a pre-defined number of prizes',
    },

    getPrizeBySlugV2: {
      method: 'GET',
      path: '/get-prize-by-slug-v2',
      query: z.object({
        slug: z.string(),
      }),
      responses: {
        200: z.object({
          prize: ZGetPrizeBySlugResponseV2,
        }),
        404: z.object({
          message: z.string(),
        }),
      },
      summary:
        'Get a prize for prize details page, includes prize tiers and winning criteria',
    },

    getSimilarPrizes: {
      method: 'GET',
      path: '/get-similar-prizes',
      query: z.object({
        prizeId: z.coerce.number(),
      }),
      responses: {
        200: ZGetSimilarPrizesResponse,
      },
      summary: 'Get similar prizes for recommendation',
    },

    incrementPrizeViewCount: {
      method: 'PATCH',
      path: '/increment-prize-view-count',
      body: z.object({
        slug: z.string(),
      }),
      responses: {
        200: successSchema,
        404: z.object({
          message: z.string(),
        }),
      },
    },

    getSearchSuggestions: {
      method: 'GET',
      path: '/search-suggestions',
      responses: {
        200: z.array(z.string()),
      },
      summary: 'Get the latest trending prizes for the search box',
    },

    getPrizesSearch: {
      method: 'GET',
      path: '/search',
      query: z.object({
        drawTypeCode: z.string(),
        drawScheduleCode: z.string(),
        term: z.string().optional(),
        tiers: z.array(z.string()).optional(), // ['gold', 'bronze']
        categories: z.array(z.string()).optional(), // ["travel", "tech"]
        limit: z.coerce.number().optional(),
        cursor: z.string().optional(),
      }),
      responses: {
        200: z.object({
          results: ZGetAllAvailablePrizesResponse,
          fallbackResults: ZGetAllAvailablePrizesResponse.optional(),
          nextCursor: z.string().optional(),
          resultCount: z.number(),
        }),
      },
      summary:
        'Search for prizes by search term, tiers, categories with limits and pagination',
    },

    getPromptPrizes: {
      method: 'GET',
      path: '/prompts',
      query: z.object({
        term: z.string(),
        drawTypeCode: z.string(),
        drawScheduleCode: z.string(),
      }),
      responses: {
        200: z.object({
          categories: z.array(
            z.object({
              categoryCode: z.string(),
            }),
          ),
          tiers: z.array(
            z.object({
              tierCode: z.string(),
            }),
          ),
          searchPrompts: z.array(
            z.object({
              id: z.number(),
              title: z.string(),
            }),
          ),
        }),
      },
      summary: 'Get search prompts as users type',
    },
  },
  {
    pathPrefix: '/public',
  },
);
